import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, Chip, TextField, CircularProgress, SxProps, Theme } from '@mui/material';
import { useLazyQuery, DocumentNode, TypedDocumentNode, OperationVariables } from '@apollo/client';
import { Controller } from 'react-hook-form';
import i18next from 'i18next';

const PAGE_SIZE = 20;

interface LazyOption {
  id: number;
  label: string;
}

interface FormInputLazyAutocompleteProps<TData, TVariables extends OperationVariables> {
  name: string;
  control: any;
  label: string;
  query: DocumentNode | TypedDocumentNode<TData, TVariables>;
  getOptions: (data: TData | undefined) => LazyOption[];
  getTotalCount: (data: TData | undefined) => number;
  buildVariables?: (search: string | null, skip: number, take: number) => TVariables;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  sx?: SxProps<Theme>;
  /** Additional variables to pass to the query (e.g. companyId for filtering clients) */
  additionalVariables?: Partial<TVariables>;
  /** Called when value changes (single-select mode) */
  onValueChange?: (value: number | null) => void;
  /** Enable multi-select mode; form value becomes number[] */
  multiple?: boolean;
  /** Known option used to display a label before lazy options are loaded */
  initialOption?: LazyOption | null;
}

/**
 * Lazy-loaded autocomplete component with pagination and search.
 * Fetches options from GraphQL query on demand with debounced search.
 * Supports infinite scroll loading and additional filter variables.
 *
 * @param query - GraphQL query document for fetching options
 * @param getOptions - Extracts option array from query response
 * @param getTotalCount - Extracts total count from query response for pagination
 * @param buildVariables - Optional custom variable builder (defaults to search/skip/take)
 * @param additionalVariables - Additional variables passed to query (e.g. companyId for filtering)
 */
export function FormInputLazyAutocomplete<TData = any, TVariables extends OperationVariables = OperationVariables>({
  name,
  control,
  label,
  query,
  getOptions,
  getTotalCount,
  buildVariables,
  disabled = false,
  allowEmpty = true,
  emptyLabel,
  sx,
  additionalVariables,
  onValueChange,
  multiple = false,
  initialOption = null,
}: FormInputLazyAutocompleteProps<TData, TVariables>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<LazyOption[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<LazyOption | null>(initialOption ?? null);
  const [selectedOptions, setSelectedOptions] = useState<LazyOption[]>([]);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const savedScrollTop = useRef(0);
  const pendingScrollRestore = useRef(false);
  // Guards handleScroll synchronously; Apollo's `loading` flag only updates after a re-render,
  // which is too late to stop multiple rapid scroll events from triggering duplicate/skipped pages.
  const isFetchingMoreRef = useRef(false);

  const defaultBuildVariables = (search: string | null, skip: number, take: number): TVariables => {
    return {
      search: search && search.length > 0 ? search : null,
      skip,
      take,
      ...additionalVariables,
    } as unknown as TVariables;
  };

  const [fetchData, { loading, data }] = useLazyQuery(query, {
    fetchPolicy: 'network-only',
    onCompleted: (newData) => {
      const newOptions = getOptions(newData);
      const totalCount = getTotalCount(newData);

      if (page === 0) {
        setOptions(newOptions);
      } else {
        setOptions(prev => {
          // Deduplicate options
          const existingIds = new Set(prev.map(o => o.id));
          const uniqueNewOptions = newOptions.filter(o => !existingIds.has(o.id));
          return [...prev, ...uniqueNewOptions];
        });
      }

      const loadedCount = (page + 1) * PAGE_SIZE;
      setHasMore(loadedCount < totalCount);
      isFetchingMoreRef.current = false;
    },
    onError: () => {
      isFetchingMoreRef.current = false;
    },
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
      setPage(0);
      setOptions([]);
      setHasMore(true);
      pendingScrollRestore.current = false;
      isFetchingMoreRef.current = false;
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Refetch when additionalVariables change (e.g. companyId)
  useEffect(() => {
    if (isOpen) {
      setPage(0);
      setOptions([]);
      setHasMore(true);
      pendingScrollRestore.current = false;
      isFetchingMoreRef.current = false;
      const vars = buildVariables
        ? buildVariables(debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null, 0, PAGE_SIZE)
        : defaultBuildVariables(debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null, 0, PAGE_SIZE);
      fetchData({ variables: vars });
    }
  }, [JSON.stringify(additionalVariables)]);

  // Fetch when debounced search changes or page changes
  useEffect(() => {
    if (isOpen) {
      const vars = buildVariables
        ? buildVariables(debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null, page * PAGE_SIZE, PAGE_SIZE)
        : defaultBuildVariables(debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null, page * PAGE_SIZE, PAGE_SIZE);
      fetchData({ variables: vars });
    }
  }, [debouncedSearch, page, isOpen, fetchData, buildVariables]);

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget;
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 &&
        hasMore &&
        !isFetchingMoreRef.current
      ) {
        isFetchingMoreRef.current = true;
        savedScrollTop.current = listboxNode.scrollTop;
        pendingScrollRestore.current = true;
        setPage(prev => prev + 1);
      }
    },
    [hasMore]
  );

  // MUI re-syncs the highlighted option whenever the option count changes and resets the listbox
  // scroll in the process. A passive effect runs after that reset, so restore the position here.
  useEffect(() => {
    if (pendingScrollRestore.current && listboxRef.current) {
      listboxRef.current.scrollTop = savedScrollTop.current;
    }
    pendingScrollRestore.current = false;
  }, [options]);

  const handleOpen = () => {
    setIsOpen(true);
    setPage(0);
    setOptions([]);
    setHasMore(true);
    pendingScrollRestore.current = false;
    isFetchingMoreRef.current = false;
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Build options list with empty option if allowed (single-select only)
  // Keep the known initial option visible even before lazy results load
  const allOptions: LazyOption[] = [
    ...(!multiple && allowEmpty ? [{ id: 0, label: emptyLabel || (i18next.t('generic-selection-empty') as string) }] : []),
    ...(initialOption && !options.some(o => o.id === initialOption.id) ? [initialOption] : []),
    ...options,
  ];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        if (multiple) {
          // Multi-select mode: value is number[]
          const valueIds: number[] = Array.isArray(value) ? value : [];

          // Keep selectedOptions in sync when value changes externally
          useEffect(() => {
            const resolved = valueIds.map(id => {
              const found = options.find(o => o.id === id);
              return found || selectedOptions.find(o => o.id === id) || { id, label: `ID: ${id}` };
            });
            setSelectedOptions(resolved);
          }, [JSON.stringify(valueIds), options]);

          return (
            <Autocomplete
              multiple
              sx={sx}
              open={isOpen}
              onOpen={handleOpen}
              onClose={handleClose}
              disabled={disabled}
              options={options}
              loading={loading}
              value={valueIds.map(id => {
                const found = options.find(o => o.id === id) || selectedOptions.find(o => o.id === id);
                return found || { id, label: `ID: ${id}` };
              })}
              filterOptions={x => x}
              getOptionLabel={option => (option as LazyOption).label}
              isOptionEqualToValue={(option, val) => (option as LazyOption).id === (val as LazyOption).id}
              inputValue={isOpen ? inputValue : ''}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') setInputValue(newInputValue);
              }}
              onChange={(_, newValues) => {
                const arr = newValues as LazyOption[];
                setSelectedOptions(arr);
                onChange(arr.map(o => o.id));
              }}
              ListboxProps={{ ref: listboxRef, onScroll: handleScroll, style: { maxHeight: 300 } }}
              renderTags={(tagValue, getTagProps) =>
                (tagValue as LazyOption[]).map((option, index) => (
                  <Chip label={option.label} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label={label}
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={(option as LazyOption).id}>
                  {(option as LazyOption).label}
                </li>
              )}
              noOptionsText={loading ? i18next.t('generic-loading') : i18next.t('generic-no-results')}
            />
          );
        }

        // Single-select mode (existing behaviour)
        useEffect(() => {
          if (value && value !== 0) {
            const found = options.find(o => o.id === value);
            if (found) {
              if (!selectedOption || selectedOption.id !== value || selectedOption.label !== found.label) {
                setSelectedOption(found);
              }
            } else if (initialOption && initialOption.id === value) {
              if (!selectedOption || selectedOption.id !== value || selectedOption.label !== initialOption.label) {
                setSelectedOption(initialOption);
              }
            }
          } else if (!value || value === 0) {
            if (selectedOption) setSelectedOption(null);
          }
        }, [value, options, initialOption]);

        const currentValue =
          value && value !== 0
            ? selectedOption ||
              (initialOption?.id === value ? initialOption : null) || { id: value, label: `ID: ${value}` }
            : allowEmpty
              ? { id: 0, label: emptyLabel || (i18next.t('generic-selection-empty') as string) }
              : null;

        return (
          <Autocomplete
            sx={sx}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            disabled={disabled}
            options={allOptions}
            loading={loading}
            value={currentValue}
            filterOptions={x => x}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            inputValue={isOpen ? inputValue : currentValue?.label || ''}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              }
            }}
            onChange={(_, newValue) => {
              const newId = newValue?.id || null;
              onChange(newId === 0 ? null : newId);
              setSelectedOption(newValue && newValue.id !== 0 ? newValue : null);
              onValueChange?.(newId === 0 ? null : newId);
            }}
            ListboxProps={{
              ref: listboxRef,
              onScroll: handleScroll,
              style: { maxHeight: 300 },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={label}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.id === 0 ? <em>{option.label}</em> : option.label}
              </li>
            )}
            noOptionsText={loading ? i18next.t('generic-loading') : i18next.t('generic-no-results')}
          />
        );
      }}
    />
  );
}
